import { useState } from "react";
import {
  ArrowLeft,
  Download,
  FileWarning,
  Loader2,
  Trash2,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import {
  DOCUMENT_SOURCE_LABELS,
  type ProjectDocument,
  type SourceBox,
  documentDisplayName,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { downloadAuthedFile } from "@/components/gb/authed-file";
import { Button } from "@/components/ui/button";
import { formatSize } from "@/features/project/document-row";
import { downloadPath, useDeleteDocument } from "@/features/project/documents-api";
import { DocumentDebug } from "@/features/debug/debug-panels";
import { formatDayOf } from "@/lib/format";
import { DocumentCategory } from "./category-picker";
import { DocumentLinks } from "./document-links";
import { DocumentStatus } from "./document-status";
import { DocumentVersion } from "./document-version";
import { cameFrom, type CameFrom } from "./file-link";
import { formatDocumentDate, revisionText } from "./family-words";
import {
  previewPath,
  useBrainDocument,
  useDocumentAssets,
  useDocumentSegments,
} from "./brain-api";
import { FilePreview } from "./file-preview";
import { DocumentReadState } from "./read-state";
import { CitedRegionPanel, parseBoxParam } from "./region-overlay";
import { SegmentsPanel } from "./segments-panel";
import { removedAtSourceLabel } from "./removed-at-source";

/**
 * One document: the file itself, and what the Brain read out of it.
 *
 * **The file is shown by the browser, not by a library.** A PDF goes in an
 * `<iframe>` pointed at a presigned inline URL, which gets real pages, zoom,
 * text search, print and `#page=41` for nothing — no PDF.js in the bundle, no
 * canvas rendering, no worker. Images and plain text are equally native.
 *
 * For a DOCX, an XLSX or a DWG there is nothing a browser can render, and this
 * is where the parsing work earns its keep: the extracted segments *are* the
 * view. A spreadsheet reads as its markdown tables, a Word document as its
 * rendered pages. Download stays prominent because it is still the only way to
 * see the original formatting.
 *
 * `?page=` arrives from a Brain search hit, so a result that says "page 41"
 * opens at page 41 rather than at the front of a seventy-five page manual.
 *
 * `&box=` arrives from a citation of a region a look read (2026-09-26, Phase
 * 5 slice V). On a picture file it is drawn on the picture. On a PDF it is
 * drawn on a picture of the page, in a "Cited region" panel above the file,
 * because nothing can draw over the browser's PDF viewer.
 */
export function DocumentViewerScreen() {
  const { docId } = useParams();
  const { projectId } = useProjectContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Where the person opened this file from. Without it, the Brain's Sources
  // tab: the files live there, and plain `/brain` redirects to Ask.
  const from: CameFrom = cameFrom(location.state) ?? {
    path: `/projects/${projectId}/brain/sources`,
    label: "Back to the Brain",
  };

  const requestedPage = Number(searchParams.get("page")) || null;
  const region = parseBoxParam(searchParams.get("box"));

  const { data: document, isPending, isError, error, refetch } = useBrainDocument(
    projectId,
    docId,
  );
  const segments = useDocumentSegments(projectId, docId);
  const assets = useDocumentAssets(projectId, docId);
  const remove = useDeleteDocument(projectId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Opening the file…</span>
        </div>
      </ContentColumn>
    );
  }

  if (isError) {
    return (
      <ContentColumn>
        <BackLink from={from} />
        <div className="mt-4 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">Couldn’t open this file.</p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">
            {error instanceof Error ? error.message : "Something went wrong."}
          </p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </ContentColumn>
    );
  }

  const reading =
    document.processingStatus === "queued" || document.processingStatus === "running";

  return (
    <ContentColumn tight>
      <BackLink from={from} />

      <header className="mt-4 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-[1.55rem] leading-[1.1] font-bold tracking-[0.01em] break-words text-ink uppercase">
            {documentDisplayName(document.filename)}
          </h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[0.85rem] text-muted">
            <span>{formatSize(document.sizeBytes)}</span>
            {document.pageCount !== null && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {document.pageCount} {document.pageCount === 1 ? "page" : "pages"}
                </span>
              </>
            )}
            <span aria-hidden>·</span>
            <span>
              {document.source === "google-drive"
                ? "Google Drive"
                : document.source === "local"
                  ? "Uploaded"
                  : DOCUMENT_SOURCE_LABELS[document.source]}
            </span>
            {/* Kept after it left its SharePoint or Outlook folder, and out of
                answers (`plans/sharepoint-sync.md` question 15,
                `plans/outlook-sync.md` question 22). */}
            {document.removedAtSource && (
              <>
                <span aria-hidden>·</span>
                <span className="font-semibold text-warn-text">
                  {removedAtSourceLabel(document.source)} on {formatDayOf(document.removedAtSource)}, not used in answers
                </span>
              </>
            )}
            {/* What the document says about itself, not when it arrived: every
                file in one bundle arrived at the same moment. */}
            {document.documentDate && (
              <>
                <span aria-hidden>·</span>
                <span>
                  Dated{" "}
                  <time dateTime={document.documentDate}>
                    {formatDocumentDate(document.documentDate)}
                  </time>
                </span>
              </>
            )}
            {document.revision && (
              <>
                <span aria-hidden>·</span>
                <span>{revisionText(document.revision)}</span>
              </>
            )}
            {reading && (
              <>
                <span aria-hidden>·</span>
                <span className="flex items-center gap-1.5 font-semibold text-brand">
                  <Loader2 size={13} className="animate-spin" aria-hidden />
                  Still reading it
                </span>
              </>
            )}
          </p>
          {/* What of it nothing read, and what no step organised (RET-12). */}
          <DocumentReadState document={document} />
          <DocumentCategory projectId={projectId} document={document} />
          {/* Above the version, because it is the bigger fact: which version a
              file is only matters once it is this project's file at all
              (`document-ai/0013`). Keyed like the version block, and for the
              same reason. */}
          <DocumentStatus
            key={`status-${document.id}`}
            projectId={projectId}
            document={document}
            onRemoved={() => void navigate(from.path)}
            from={cameFrom(location.state) ?? undefined}
            folded
          />
          {/* Keyed by the file: its links open another file on this same
              screen, and a half-answered Undo must not carry over to it. */}
          <DocumentVersion
            // Its own key: two siblings with one key are one child to React.
            key={`version-${document.id}`}
            projectId={projectId}
            document={document}
            from={cameFrom(location.state) ?? undefined}
          />
          {/* The files it is connected to (`document-ai/0024`). Nothing when
              there are none, which is most files; folded when there are some,
              on one generic line (§20). */}
          <DocumentLinks
            key={`links-${document.id}`}
            projectId={projectId}
            documentId={document.id}
            from={cameFrom(location.state) ?? undefined}
          />
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <DownloadButton projectId={projectId} document={document} />
          {confirmingDelete ? (
            <span className="flex items-center gap-1.5">
              <span className="text-[0.78rem] font-semibold text-danger">Delete file?</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={remove.isPending}
              >
                No
              </Button>
              <Button
                size="sm"
                className="bg-danger hover:bg-danger"
                disabled={remove.isPending}
                onClick={async () => {
                  await remove.mutateAsync(document.id);
                  void navigate(from.path);
                }}
              >
                Yes
              </Button>
            </span>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setConfirmingDelete(true)}>
              <Trash2 size={15} />
              Delete
            </Button>
          )}
        </div>
      </header>

      {remove.error && (
        <p role="alert" className="mb-4 text-[0.85rem] font-semibold text-danger">
          {remove.error.message}
        </p>
      )}

      {/* The machinery behind this file, with Debug on (`architecture/0029`).
          Renders nothing otherwise. Above the preview, so it is beside the
          facts it explains rather than under a page of drawing. */}
      <div className="mb-5">
        <DocumentDebug
          key={`debug-${document.id}`}
          projectId={projectId}
          documentId={document.id}
          from={cameFrom(location.state) ?? undefined}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <FilePane document={document} projectId={projectId} page={requestedPage} region={region} />
        <SegmentsPanel
          projectId={projectId}
          documentId={document.id}
          segments={segments.data ?? []}
          assets={assets.data ?? []}
          isPending={segments.isPending}
          processingStatus={document.processingStatus}
          highlightPage={requestedPage}
        />
      </div>
    </ContentColumn>
  );
}

/**
 * The original file, shown by whatever the browser can do with it.
 *
 * `previewType` decides, not `mimeType`. A `.docx` is rendered to PDF in order
 * to be read at all, and that render is now kept — so a Word document displays
 * as its own pages, which are also the pages its citations name. This pane used
 * to argue that "no preview plus the segments panel" was the complete answer
 * for a DOCX; the render was there the whole time and was being deleted.
 *
 * The empty case survives for what genuinely has none: a DWG, or a `.docx`
 * parsed on a container with no LibreOffice. There it is still not a failure
 * state — it is the case the extraction pipeline exists for.
 */
function FilePane({
  document,
  projectId,
  page,
  region,
}: {
  document: ProjectDocument;
  projectId: string;
  page: number | null;
  /** The region a look read, from `&box=`. */
  region: SourceBox | null;
}) {
  if (!document.previewType) {
    return (
      <section className="flex min-h-[320px] flex-col items-center justify-center rounded-card-lg border-[1.5px] border-border bg-surface-sunken px-6 py-10 text-center">
        <FileWarning size={30} className="text-muted" aria-hidden />
        <p className="mt-3 max-w-[42ch] text-[0.92rem] font-semibold text-ink">
          No browser can display this format
        </p>
        <p className="mt-1.5 max-w-[46ch] text-[0.85rem] leading-[1.5] text-ink-prose">
          The Brain has still read it — the pages are beside this. Download the file to see
          it as it was written.
        </p>
        <DownloadButton projectId={projectId} document={document} className="mt-4" />
      </section>
    );
  }

  const picture = document.previewType.startsWith("image/");
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* A region on a PDF is drawn on a picture of its page. A box with no
          page is taken as page 1, the only page a one-page file has. The
          panel is not drawn when the page has no picture. */}
      {region && !picture && (
        <CitedRegionPanel projectId={projectId} documentId={document.id} page={page ?? 1} box={region} />
      )}
      <section className="overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface-sunken">
        <FilePreview
          path={previewPath(projectId, document.id)}
          type={document.previewType}
          filename={document.filename}
          page={page}
          box={picture ? region : null}
        />
      </section>
    </div>
  );
}

/**
 * Save the original.
 *
 * A button rather than an `<a href>`: the download route is behind the guard
 * and a top-level navigation sends no `Authorization` header, so the link
 * answered 401 and navigated to the error instead of saving the file.
 */
function DownloadButton({
  projectId,
  document,
  className,
}: {
  projectId: string;
  document: ProjectDocument;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className={className}
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setFailed(false);
          downloadAuthedFile(downloadPath(projectId, document.id), document.filename)
            .catch(() => setFailed(true))
            .finally(() => setBusy(false));
        }}
      >
        <Download size={15} />
        {busy ? "Preparing…" : "Download"}
      </Button>
      {failed && (
        <p role="alert" className="mt-2 text-[0.8rem] font-semibold text-danger">
          That file couldn’t be downloaded.
        </p>
      )}
    </>
  );
}

function BackLink({ from }: { from: CameFrom }) {
  return (
    <Link
      to={from.path}
      className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft size={15} />
      {from.label}
    </Link>
  );
}
